import React, { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';
import { Button } from './ui/button';
import { Checkbox } from './ui/checkbox';
import { Label } from './ui/label';
import { Shield, ExternalLink, Building2, AlertCircle } from 'lucide-react';
import axios from 'axios';
import { API } from '../App';
import { toast } from 'sonner';

const PROVIDER_INFO = {
  mono: {
    region: 'Nigeria, Ghana, and Kenya',
    privacyUrl: 'https://mono.co/privacy',
    privacyLabel: 'Bank sync provider privacy policy',
    dataAccessed: [
      'Account balances',
      'Transaction history (up to 12 months)',
      'Account holder name',
      'Account numbers (masked)'
    ]
  },
  plaid: {
    region: 'UK, US, Canada and Europe',
    privacyUrl: 'https://plaid.com/legal',
    privacyLabel: 'Bank sync provider privacy policy',
    dataAccessed: [
      'Account balances',
      'Transaction history',
      'Account holder identity',
      'Account and routing numbers (masked)'
    ]
  },
  stitch: {
    region: 'South Africa',
    privacyUrl: 'https://www.stitch.money/legal/privacy-policy',
    privacyLabel: 'Bank sync provider privacy policy',
    dataAccessed: [
      'Account balances',
      'Transaction history',
      'Account details',
      'Payment initiation (if enabled)'
    ]
  }
};

const OpenBankingConsentDialog = ({ 
  open, 
  onOpenChange, 
  provider = 'mono',
  onConsent,
  onCancel 
}) => {
  const [agreed, setAgreed] = useState(false);
  const [loading, setLoading] = useState(false);
  
  const providerInfo = PROVIDER_INFO[provider] || PROVIDER_INFO.mono;

  const handleConsent = async () => {
    if (!agreed) {
      toast.error('Please confirm you understand and agree');
      return;
    }

    setLoading(true);
    try {
      // Log consent to backend
      await axios.post(`${API}/privacy/log-open-banking-consent`, {
        provider: provider,
        account_types: ['transactions', 'balances', 'identity'],
        consent_given: true
      });

      // Proceed with bank connection
      if (onConsent) {
        onConsent();
      }
      onOpenChange(false);
    } catch (error) {
      console.error('Failed to log consent:', error);
      // Still proceed - don't block user
      if (onConsent) {
        onConsent();
      }
      onOpenChange(false);
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    setAgreed(false);
    if (onCancel) {
      onCancel();
    }
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg" data-testid="open-banking-consent-dialog">
        <DialogHeader>
          <div className="flex items-center gap-3 mb-2">
            <div className="w-12 h-12 rounded-full bg-[#108981]/10 flex items-center justify-center">
              <Building2 className="w-6 h-6 text-[#108981]" />
            </div>
            <div>
              <DialogTitle>Connect your bank securely</DialogTitle>
              <DialogDescription>Authorize Bank Sync for {providerInfo.region}</DialogDescription>
            </div>
          </div>
        </DialogHeader>

        <div className="space-y-4 py-4">
          {/* Data Access Info */}
          <div className="bg-muted/50 rounded-lg p-4">
            <div className="flex items-center gap-2 mb-3">
              <Shield className="w-5 h-5 text-[#108981]" />
              <p className="font-medium">Data that will be accessed:</p>
            </div>
            <ul className="space-y-2 ml-7">
              {providerInfo.dataAccessed.map((item, index) => (
                <li key={index} className="text-sm text-muted-foreground flex items-center gap-2">
                  <span className="w-1.5 h-1.5 rounded-full bg-[#108981]"></span>
                  {item}
                </li>
              ))}
            </ul>
          </div>

          {/* Security Notice */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <p className="text-sm text-blue-800">
              <Shield className="w-4 h-4 inline mr-2" />
              <strong>Your bank credentials are never stored by AkountFly.</strong> All connections 
              are handled through a regulated financial data provider using bank-level encryption.
            </p>
          </div>

          {/* How Data is Used */}
          <div>
            <p className="font-medium mb-2">How AkountFly uses this data:</p>
            <ul className="text-sm text-muted-foreground space-y-1 ml-4">
              <li>• Automatically import your bank transactions</li>
              <li>• Match transactions to invoices and bills</li>
              <li>• Generate accurate financial reports</li>
              <li>• Provide AI-powered categorization</li>
            </ul>
          </div>

          {/* Privacy Policy Link */}
          <div className="flex items-center gap-2 text-sm">
            <span className="text-muted-foreground">Review the {providerInfo.privacyLabel}:</span>
            <a 
              href={providerInfo.privacyUrl} 
              target="_blank" 
              rel="noopener noreferrer"
              className="text-[#108981] hover:underline flex items-center gap-1"
            >
              Privacy Policy <ExternalLink className="w-3 h-3" />
            </a>
          </div>

          {/* Consent Checkbox */}
          <div className="flex items-start gap-3 p-4 border rounded-lg">
            <Checkbox 
              id="consent-checkbox"
              checked={agreed}
              onCheckedChange={setAgreed}
              data-testid="open-banking-consent-checkbox"
            />
            <Label htmlFor="consent-checkbox" className="text-sm leading-relaxed cursor-pointer">
              I understand that my bank data will be accessed through a regulated financial data
              provider and processed by AkountFly as described above. I authorize this connection.
            </Label>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={handleCancel}>
            Cancel
          </Button>
          <Button 
            onClick={handleConsent}
            disabled={!agreed || loading}
            className="bg-[#108981] hover:bg-[#108981]/90 gap-2"
            data-testid="authorize-open-banking-btn"
          >
            {loading ? (
              <span className="animate-spin">⏳</span>
            ) : (
              <Shield className="w-4 h-4" />
            )}
            I Authorize
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default OpenBankingConsentDialog;
